import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import VoiceInput from "@/components/VoiceInput";
import { BookMarked, Plus, Sparkles, Smile, Meh, Frown, MessageCircle, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { es, ca } from "date-fns/locale";
import { trackJournalEntry } from "@/lib/analytics";

const Journal = () => {
  const { language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ content: "", mood: "neutral" });
  const [loading, setLoading] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [weeklyReflection, setWeeklyReflection] = useState(null);
  const [loadingReflection, setLoadingReflection] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  const [lastSavedEntry, setLastSavedEntry] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState({});

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/journal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching journal entries:", error);
      toast.error("Error al cargar entradas");
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.content.trim()) return;

    // Guardar el contingut per si falla
    const savedContent = newEntry.content;
    const savedMood = newEntry.mood;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/journal`, newEntry, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // 30 segons timeout (ara és més ràpid sense IA)
      });
      setEntries([response.data, ...entries]);
      setNewEntry({ content: "", mood: "neutral" });
      setShowNewEntry(false);
      setLastSavedEntry(response.data); // Guardar per mostrar el botó d'anar al xat
      trackJournalEntry();
      toast.success(language === "es" ? "¡Entrada guardada!" : "Entrada desada!");
    } catch (error) {
      console.error("Error creating entry:", error);
      // Restaurar el contingut per si l'usuari vol reintentar
      setNewEntry({ content: savedContent, mood: savedMood });
      toast.error(language === "es" 
        ? "Error al guardar. Tu texto no se ha perdido, inténtalo de nuevo." 
        : "Error en desar. El teu text no s'ha perdut, intenta-ho de nou.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoToChat = (entry) => {
    // Navegar al xat amb el context de l'entrada del diari
    const entryPreview = entry.content.substring(0, 100);
    navigate(`/chat?context=journal&id=${entry.id}&title=${encodeURIComponent(entryPreview)}`);
  };

  const handleGetWeeklyReflection = async () => {
    setLoadingReflection(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/journal/weekly-reflection`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyReflection(response.data);
      setShowReflection(true);
      toast.success(t("¡Reflexión generada!", "Reflexió generada!"));
    } catch (error) {
      console.error("Error generating reflection:", error);
      const errorMsg = error.response?.data?.detail || t("Error al generar reflexión", "Error al generar reflexió");
      toast.error(errorMsg);
    } finally {
      setLoadingReflection(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const getMoodIcon = (mood) => {
    switch (mood) {
      case "happy":
        return <Smile className="w-5 h-5 text-green-400" />;
      case "sad":
        return <Frown className="w-5 h-5 text-blue-400" />;
      default:
        return <Meh className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = language === "ca" ? ca : es;
    return format(date, "d 'de' MMMM, yyyy - HH:mm", { locale });
  };

  if (loadingEntries) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div data-testid="journal-header" className="mb-8 fade-in">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold glow-text">
                {t("Mi Diario", "El Meu Diari")}
              </h1>
              <p className="text-lg text-gray-300 mt-2">
                {t("Reflexiona y crece con consciencia", "Reflexiona i creix amb consciència")}
              </p>
            </div>
            <button
              data-testid="new-entry-btn"
              onClick={() => setShowNewEntry(!showNewEntry)}
              className="btn-primary px-6 py-3 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">{t("Nueva Entrada", "Nova Entrada")}</span>
            </button>
          </div>

          {/* Weekly Reflection Button */}
          {entries.length > 0 && (
            <button
              onClick={handleGetWeeklyReflection}
              disabled={loadingReflection}
              className="btn-secondary px-6 py-3 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingReflection ? (
                <>
                  <div className="w-5 h-5 border-2 border-purple-300/30 border-t-purple-300 rounded-full animate-spin"></div>
                  <span>{t("Generando...", "Generant...")}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>{t("Obtener Reflexión Semanal", "Obtenir Reflexió Setmanal")}</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Weekly Reflection Display */}
        {showReflection && weeklyReflection && (
          <div className="card glass rounded-2xl p-6 mb-8 border-purple-400/30 fade-in">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-purple-300" />
                <h3 className="text-xl font-semibold text-purple-200">
                  {t("Reflexión sobre tu Evolución", "Reflexió sobre la teva Evolució")}
                </h3>
              </div>
              <button
                onClick={() => setShowReflection(false)}
                className="text-gray-400 hover:text-gray-300 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            <p className="text-sm text-gray-400 mb-4">
              {t(
                `Basado en ${weeklyReflection.entries_analyzed} entradas de las últimas 2 semanas`,
                `Basat en ${weeklyReflection.entries_analyzed} entrades de les últimes 2 setmanes`
              )}
            </p>
            
            <div className="bg-black/30 rounded-xl p-6 border border-purple-400/10">
              <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                {weeklyReflection.reflection}
              </p>
            </div>
          </div>
        )}

        {/* New Entry Form */}
        {showNewEntry && (
          <div data-testid="new-entry-form" className="glass rounded-3xl p-6 mb-8 border border-teal-400/20 fade-in">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t("¿Cómo te sientes?", "Com et sents?")}
                </label>
                <div className="flex gap-3">
                  {["happy", "neutral", "sad"].map((mood) => (
                    <button
                      key={mood}
                      type="button"
                      data-testid={`mood-${mood}-btn`}
                      onClick={() => setNewEntry({ ...newEntry, mood })}
                      className={`flex-1 p-3 rounded-xl border transition-all ${
                        newEntry.mood === mood
                          ? "bg-teal-400/20 border-teal-400/50"
                          : "bg-black/20 border-gray-700 hover:border-gray-600"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {getMoodIcon(mood)}
                        <span className="text-sm text-gray-300">
                          {mood === "happy" && t("Feliz", "Feliç")}
                          {mood === "neutral" && t("Neutral", "Neutral")}
                          {mood === "sad" && t("Triste", "Trist")}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-2">
                  {t("Tu reflexión", "La teva reflexió")}
                </label>
                <div className="relative">
                  <textarea
                    id="content"
                    data-testid="journal-content-input"
                    value={newEntry.content}
                    onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                    rows={6}
                    placeholder={t("Escribe tus pensamientos...", "Escriu els teus pensaments...")}
                    className="w-full px-4 py-3 pr-14 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 resize-none transition-all"
                    required
                  />
                  <div className="absolute bottom-3 right-3">
                    <VoiceInput 
                      onTranscription={(text) => setNewEntry({ ...newEntry, content: newEntry.content + (newEntry.content ? ' ' : '') + text })}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  data-testid="save-entry-btn"
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary py-3 disabled:opacity-50"
                >
                  {loading 
                    ? t("Desant...", "Desant...") 
                    : t("Guardar", "Desar")}
                </button>
                <button
                  data-testid="cancel-entry-btn"
                  type="button"
                  onClick={() => {
                    setShowNewEntry(false);
                    setNewEntry({ content: "", mood: "neutral" });
                  }}
                  className="btn-secondary px-6 py-3"
                >
                  {t("Cancelar", "Cancel·lar")}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Success message with option to discuss with AI */}
        {lastSavedEntry && !showNewEntry && (
          <div data-testid="saved-entry-success" className="glass rounded-3xl p-6 mb-8 border border-green-400/20 fade-in">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-green-300 mb-1">
                  {t("✓ Entrada guardada", "✓ Entrada desada")}
                </h3>
                <p className="text-sm text-gray-400">
                  {t("¿Quieres explorar más profundamente lo que has escrito?", "Vols explorar més profundament el que has escrit?")}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="discuss-with-ai-btn"
                  onClick={() => handleGoToChat(lastSavedEntry)}
                  className="px-5 py-2.5 flex items-center gap-2 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 text-purple-300 transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>{t("Parlar amb la IA", "Parlar amb la IA")}</span>
                </button>
                <button
                  onClick={() => setLastSavedEntry(null)}
                  className="text-gray-400 hover:text-gray-300 px-3 py-2"
                >
                  {t("Cerrar", "Tancar")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Entries List */}
        <div data-testid="entries-list" className="space-y-6">
          {entries.length === 0 ? (
            <div className="glass rounded-3xl p-12 text-center border border-gray-800">
              <BookMarked className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">
                {t("Aún no tienes entradas. ¡Crea tu primera reflexión!", "Encara no tens entrades. Crea la teva primera reflexió!")}
              </p>
            </div>
          ) : (
            entries.map((entry, index) => (
              <div
                key={entry.id}
                data-testid={`entry-${index}`}
                className="glass rounded-2xl overflow-hidden border border-amber-400/10 fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Entry header - always visible, clickable */}
                <button
                  onClick={() => setExpandedEntries(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {getMoodIcon(entry.mood)}
                    <span className="text-sm text-gray-400 flex-shrink-0">{formatDate(entry.created_at)}</span>
                    {!expandedEntries[entry.id] && (
                      <span className="text-sm text-gray-500 truncate">
                        {entry.content.substring(0, 50)}{entry.content.length > 50 ? '...' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedEntries[entry.id] ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Entry content - expandable */}
                {expandedEntries[entry.id] && (
                  <div className="px-4 pb-4 space-y-3 fade-in">
                    <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                      {entry.content}
                    </p>

                    {entry.ai_reflection && (
                      <div className="bg-black/30 rounded-xl p-4 border border-teal-400/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-teal-300" />
                          <span className="text-sm font-semibold text-teal-300">
                            {t("Reflexión anterior", "Reflexió anterior")}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed italic">
                          {entry.ai_reflection}
                        </p>
                      </div>
                    )}

                    <button
                      data-testid={`chat-entry-${index}-btn`}
                      onClick={() => handleGoToChat(entry)}
                      className="flex items-center gap-1.5 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>{t("Parlar amb la IA", "Parlar amb la IA")}</span>
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Journal;